import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../hooks/use-toast';
import { ArrowLeft, BadgeCheck, Download, Calendar, FileText } from 'lucide-react';
import PortalRequestDocs from './PortalRequestDocs';
import { downloadBlob, isIOS } from '../../utils/downloadBlob';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const PortalMyLetters = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/employee-portal/my-certificates`, { headers });
      setItems(r.data?.items || []);
    } catch (e) {
      if (e?.response?.status === 401) navigate('/employee-portal/login');
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const KIND_LABEL = { experience: 'Experience Certificate', salary: 'Salary Certificate', noc: 'No Objection Certificate' };

  const download = async (cert) => {
    try {
      const r = await axios.get(`${API}/api/employee-portal/my-certificates/${cert.id}/pdf`, {
        headers, responseType: 'blob',
      });
      const base = { salary: 'Salary_Certificate', noc: 'NOC', experience: 'Employment_Certificate' }[cert.kind] || 'Certificate';
      const filename = `${base}_${(cert.reference_number || 'cert').replace(/\//g, '-')}.pdf`;
      downloadBlob(r.data, filename, 'application/pdf');
      toast({
        title: isIOS() ? 'PDF opened' : 'Downloaded',
        description: isIOS() ? 'Tap the Share button to save or print.' : undefined,
      });
    } catch {
      toast({ title: 'Download failed', variant: 'destructive' });
    }
  };

  return (
    <div className="p-4 max-w-2xl mx-auto" data-testid="portal-my-letters">
      <button onClick={() => navigate('/employee-portal')} className="flex items-center gap-1 text-sm text-violet-600 mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to Home
      </button>

      <div className="flex items-center gap-3 mb-5">
        <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
          <BadgeCheck className="h-6 w-6 text-amber-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">My Letters</h1>
          <p className="text-xs text-gray-500">Employment & experience certificates issued to you</p>
        </div>
      </div>

      <div className="mb-4">
        <PortalRequestDocs />
      </div>

      {loading ? (
        <Card><CardContent className="py-12 text-center text-gray-500">Loading…</CardContent></Card>
      ) : items.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <BadgeCheck className="h-12 w-12 mx-auto text-amber-300 mb-3" />
            <p className="text-gray-600">No letters issued to you yet.</p>
            <p className="text-xs text-gray-400 mt-1">When HR sends you a certificate, it will appear here.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map(c => (
            <Card key={c.id} className="border-0 shadow-sm hover:shadow-md transition" data-testid={`letter-${c.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="h-12 w-12 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <FileText className="h-6 w-6 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px] font-mono">{c.reference_number}</Badge>
                      {c.is_currently_working && (
                        <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Currently Working</Badge>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 mt-1">{KIND_LABEL[c.kind] || 'Certificate'}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {(c.designation || c.purpose_type === 'travel' ? c.destination_country : '') ? `${c.designation || c.destination_country} • ` : ''}Issued {formatDate(c.issue_date)}
                    </p>
                    <p className="text-[11px] text-gray-400 mt-1 flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> Sent on {formatDate(c.shared_at)}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => download(c)}
                  className="w-full mt-3 bg-amber-600 hover:bg-amber-700"
                  data-testid={`letter-download-${c.id}`}
                >
                  <Download className="h-4 w-4 mr-1" /> Download PDF
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PortalMyLetters;
